import React, { useEffect, useId } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { FaTimes } from 'react-icons/fa';

// Stack of the currently-open modals, shared by every instance.
//
// It replaces two things the per-instance effect got wrong. First, the scroll
// lock: the old cleanup reset `body.overflow` to '' unconditionally, so closing
// a modal opened on top of another one unlocked the page while the first was
// still open and the background scrolled behind it. Only the last modal to
// close now restores the original value. Second, Escape: without knowing the
// order, one key press closed every open modal at once; now only the top of the
// stack reacts.
const modalStack = [];
let bodyOverflowBeforeLock = '';

const Modal = ({
  show,
  onHide,
  title,
  children,
  footer,
  size = 'md',
  closeButton = true,
  backdropClick = true,
  fullscreen = false,
  className = '',
}) => {
  const titleId = useId();

  // Identity for this instance's entry in `modalStack` — a ref so it survives
  // re-renders and so `onHide` can change without reshuffling the stack.
  const tokenRef = React.useRef({});
  const onHideRef = React.useRef(onHide);
  onHideRef.current = onHide;

  useEffect(() => {
    if (!show) return undefined;

    const token = tokenRef.current;
    if (modalStack.length === 0) {
      bodyOverflowBeforeLock = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    modalStack.push(token);

    // Escape closes the top-most modal. The listener is on the document, not
    // the dialog, so it works before anything inside has been focused — the
    // normal case, since this component does not autofocus.
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (modalStack[modalStack.length - 1] !== token) return;
      onHideRef.current();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const i = modalStack.indexOf(token);
      if (i !== -1) modalStack.splice(i, 1);
      if (modalStack.length === 0) {
        document.body.style.overflow = bodyOverflowBeforeLock;
      }
    };
  }, [show]);

  if (!show) return null;

  const sizeClasses = {
    sm: 'tw-max-w-sm',
    md: 'tw-max-w-md',
    lg: 'tw-max-w-lg',
    xl: 'tw-max-w-2xl',
    xxl: 'tw-max-w-5xl',
    xxxl: 'tw-max-w-7xl',
    full: 'tw-max-w-4xl',
  };

  const handleBackdropClick = (e) => {
    if (backdropClick && e.target === e.currentTarget) {
      onHide();
    }
  };

  const dialogClass = fullscreen
    ? 'tw-w-screen tw-max-w-none tw-rounded-none tw-m-0'
    : `${sizeClasses[size]} tw-rounded`;

  // `dvh`, not `vh`: on mobile browsers `vh` is measured against the taller
  // URL-bar-retracted viewport, so `90vh` could exceed what is actually on
  // screen and push the footer's Save/Cancel row out of reach.
  const dialogStyle = fullscreen
    ? { height: '100dvh', zIndex: 100000 }
    : { maxHeight: '90dvh', zIndex: 100000 };

  const modal = (
    <div
      className={`tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center ${fullscreen ? '' : 'tw-p-4'} tw-bg-black tw-bg-opacity-50`}
      style={{ zIndex: 99999 }}
      onClick={handleBackdropClick}
    >
      <div
        className={`tw-bg-white tw-shadow-xl tw-w-full tw-flex tw-flex-col tw-overflow-hidden ${dialogClass} ${className}`}
        onClick={(e) => e.stopPropagation()}
        style={dialogStyle}
        role="dialog"
        aria-modal="true"
        {...(title ? { 'aria-labelledby': titleId } : {})}
      >
        {/* Header */}
        {title && (
          <div className="tw-px-6 tw-py-4 tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-flex tw-items-center tw-justify-between tw-shrink-0">
            <h2 id={titleId} className="tw-text-lg tw-font-semibold tw-text-slate-900">{title}</h2>
            {closeButton && (
              <button
                onClick={onHide}
                className="ptl-tap tw-bg-transparent tw-border-0 tw-text-slate-500 hover:tw-text-slate-700 tw-cursor-pointer tw-p-1 tw-rounded-sm hover:tw-bg-slate-200 tw-transition-colors"
                aria-label="Close modal"
              >
                <FaTimes size={18} />
              </button>
            )}
          </div>
        )}

        {/* Body. `overscroll-contain` stops the scroll from chaining to the
            page once the body hits its end — on iOS the `body { overflow:
            hidden }` lock above does not by itself prevent that. */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-overscroll-contain tw-px-6 tw-py-4">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            className="tw-px-6 tw-py-4 tw-border-t tw-border-slate-200 tw-bg-slate-50 tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2 tw-shrink-0"
            // `flex-wrap`: three action buttons in a row overflowed a 375px
            // viewport. Bottom inset only matters for `fullscreen`, where the
            // dialog reaches the edge of the screen; --safe-bottom is 0px
            // otherwise.
            style={fullscreen ? { paddingBottom: 'calc(1rem + var(--safe-bottom))' } : undefined}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

Modal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.node,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'xxl', 'xxxl', 'full']),
  closeButton: PropTypes.bool,
  backdropClick: PropTypes.bool,
  fullscreen: PropTypes.bool,
  className: PropTypes.string,
};

export default Modal;
